/**
 * PushAutoSubscribe.jsx  ·  v3.21-1800
 *
 * Silent, self-healing push registration for the authenticated PWA.
 *
 * v3.19-1418 (predecessor) only re-subscribed the BROWSER when
 * PushManager.getSubscription() returned null.  That left a second
 * failure mode uncovered: the browser has a live subscription but the
 * BACKEND row for that endpoint/user pair is missing or stale (fresh
 * install of the PWA, cleared cookies, JWT rotation, or the operator
 * signing in on a device that previously belonged to another user).
 *
 * v3.21-1800 upgrades registration to be self-healing in BOTH
 * directions:
 *
 *   authenticated boot
 *   → serviceWorker.ready  (SW is guaranteed running)
 *   → PushManager.getSubscription()
 *       ├── exists  → POST /push/subscribe (idempotent server upsert
 *       │             on `endpoint`; associates the subscription to
 *       │             the CURRENT authenticated user).
 *       └── missing → pushManager.subscribe({ applicationServerKey })
 *                     using the VAPID public key served by the Hub,
 *                     then POST /push/subscribe.
 *
 * We never prompt for permission here.  If `Notification.permission`
 * is `default` or `denied` we do nothing — the operator must grant
 * permission via the Notification Preferences page (initial opt-in
 * still requires a user gesture per browser policy).
 *
 * Bounded by `didRunRef` so the effect fires exactly once per mount.
 * On registration failure we set a small window-scoped health hint
 * (`window.__hubPushRepairNeeded = true`) so Notification Preferences
 * can surface a Repair Push control without additional round-trips.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pushSupported, subscribeCurrentDevice } from '../lib/push';

export default function PushAutoSubscribe() {
  const { user } = useAuth();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    if (!user) return;
    if (!pushSupported()) return;
    if (typeof Notification === 'undefined') return;
    // Never prompt from a background effect — grants require a user
    // gesture from the Notification Preferences UI.
    if (Notification.permission !== 'granted') return;
    didRunRef.current = true;

    (async () => {
      try {
        // subscribeCurrentDevice(deviceLabel) internally:
        //   · awaits registerServiceWorker()
        //   · calls pushManager.getSubscription()
        //   · if missing, subscribes with applicationServerKey
        //   · POSTs /push/subscribe (backend upserts on `endpoint`)
        // Calling it unconditionally here therefore re-registers the
        // existing browser subscription with the backend for the
        // current authenticated user AND creates one if missing.
        await subscribeCurrentDevice(navigator.userAgent.slice(0, 80));
        try { window.__hubPushRepairNeeded = false; } catch (_e) { /* noop */ }
        console.debug('[push v1800] self-heal OK (existing or newly created subscription upserted)');
      } catch (e) {
        try { window.__hubPushRepairNeeded = true; } catch (_e) { /* noop */ }
        console.debug('[push v1800] self-heal deferred:', e?.message || e);
      }
    })();
  }, [user]);

  return null;
}
